import { useRef, useState } from "react";
import { Button, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeVersion } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateThemeVersionMetaMutation } from "../../services/useUpdateThemeVersionMetaMutation";
import { useCreateThemeThumbnailUploadMutation } from "../../services/useCreateThemeThumbnailUploadMutation";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * The version's gallery thumbnail — the image sellers see on the theme card.
 *
 * Upload flow (mirrors the store-logo / payment-proof uploads): pick a file →
 * request a presigned PUT (`/admin/uploads/theme-thumbnails`) → PUT the file to
 * R2 → persist the returned `publicUrl` into the version's `thumbnailUrl`
 * metadata.
 *
 * Deliberately separate from the theme JSON and from Save: `thumbnailUrl` is
 * version METADATA, and the backend allows writing it even on a published
 * (frozen) version — immutability is about the rendered payload, not the
 * marketing image. That is also why ThemeStudio renders this field regardless
 * of read-only: a published theme's thumbnail must stay editable.
 */
export function ThumbnailField({
  themeId,
  version,
}: {
  themeId: string;
  version: ThemeVersion;
}) {
  const t = useTranslations();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(version.thumbnailUrl ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const { mutateAsync: createUpload } =
    useCreateThemeThumbnailUploadMutation();
  const { mutateAsync: saveMeta } = useUpdateThemeVersionMetaMutation();

  const hasImage = value.trim().length > 0;

  const genericError = (err: unknown) =>
    err instanceof ApiJsonClientError
      ? err.message || t(keys.errors.generic)
      : t(keys.errors.generic);

  const handleFile = async (file: File) => {
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t(keys.products.uploadFailed));
      return;
    }
    setIsBusy(true);
    try {
      const intent = await createUpload({
        themeId,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      try {
        const putResponse = await fetch(intent.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!putResponse.ok) {
          setError(t(keys.products.uploadFailed));
          return;
        }
      } catch (uploadError) {
        setError(
          uploadError instanceof TypeError
            ? t(keys.errors.uploadBlockedByBrowser)
            : t(keys.products.uploadFailed),
        );
        return;
      }
      const updated = await saveMeta({
        themeId,
        versionId: version.id,
        thumbnailUrl: intent.publicUrl,
      });
      setValue(updated.thumbnailUrl ?? intent.publicUrl);
      toast.success(t(keys.themes.saveSuccess));
    } catch (err) {
      setError(genericError(err));
    } finally {
      setIsBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    setError(null);
    setIsBusy(true);
    try {
      const updated = await saveMeta({
        themeId,
        versionId: version.id,
        thumbnailUrl: "",
      });
      setValue(updated.thumbnailUrl ?? "");
      toast.success(t(keys.themes.saveSuccess));
    } catch (err) {
      setError(genericError(err));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3">
      <span className="text-[12px] font-bold text-[color:var(--text-primary)]">
        {t(keys.themes.thumbnail)}
      </span>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={isBusy}
          onClick={() => inputRef.current?.click()}
          className="h-9 rounded-full px-4 text-xs font-semibold"
        >
          {isBusy
            ? t(keys.products.uploadingImage)
            : hasImage
              ? t(keys.seller.storeLogoReplace)
              : t(keys.common.upload)}
        </Button>
        {hasImage ? (
          <Button
            type="button"
            variant="ghost"
            disabled={isBusy}
            onClick={handleRemove}
            className="h-9 rounded-full px-4 text-xs font-semibold"
          >
            {t(keys.common.remove)}
          </Button>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1.5 text-[11.5px] text-[color:var(--error-600)]">
          {error}
        </p>
      ) : null}
      {hasImage ? (
        <img
          src={value}
          alt={t(keys.themes.thumbnailPreviewAlt)}
          className="mt-3 max-h-32 w-auto rounded-lg border border-[color:var(--border-light)] object-contain"
        />
      ) : null}
    </div>
  );
}
